import type * as React from "react";

import { PaperPanel, type PaperPanelProps } from "./PaperPanel";
import { cn } from "../lib/cn";

export interface SettingsCardProps extends Omit<
  PaperPanelProps,
  "title" | "children"
> {
  /** What this card controls, in three or four words. */
  title: React.ReactNode;
  /** One line of what it does, in Brian's voice. Skip it if the title says it. */
  subtitle?: React.ReactNode;
  /**
   * A glyph in a recess medallion beside the heading, for a card that names a
   * piece of hardware or a connection rather than a preference. Decorative:
   * hidden from AT, because the heading already says what the card is.
   */
  icon?: React.ReactNode;
  /** `h2` by default. Drop it to `h3`/`h4` where the page nests these deeper. */
  headingLevel?: 2 | 3 | 4;
  /** The controls. Whatever this card is for. */
  children?: React.ReactNode;
}

/**
 * One block of settings on a sheet of paper: a heading, a line of explanation,
 * and the controls under them. The unit a settings page is built from - clinic
 * preferences, connector pairing, branding.
 *
 * Spacing is fixed rather than a variant, because the two spacings the app grew
 * were an accident and not a distinction. `p-4` with a `mt-1` subtitle wins over
 * `p-5`/`mt-0.5`: `p-4` continues the paper scale the rest of the kit is on
 * (`p-3` for a record card, `p-4` for a panel of them), where `p-5` is a step
 * the scale does not otherwise take; and `mt-0.5` is 2px, which under a 16px
 * display heading reads as a crammed second line of the title rather than a
 * subtitle. Cards that want more air inside get it from the gaps between their
 * own controls, not from a wider margin around everything.
 *
 * Padding is a real override: `className="p-6"` reaches the panel, since this
 * card holds no full-bleed target that would have to restate it.
 */
export function SettingsCard({
  title,
  subtitle,
  icon,
  headingLevel = 2,
  children,
  className,
  ...rest
}: SettingsCardProps) {
  // h2/h3/h4 are the same box; the level is an outline decision, not a style.
  const Heading = `h${headingLevel}` as "h2";

  return (
    <PaperPanel className={cn("p-4 font-body", className)} {...rest}>
      <div className={cn(icon != null && "flex items-start gap-3")}>
        {icon != null && (
          <span
            aria-hidden="true"
            className="flex size-10 shrink-0 items-center justify-center rounded-full bg-recess text-annotation"
          >
            {icon}
          </span>
        )}
        {/* min-w-0 so a long heading wraps inside the row instead of pushing
            the medallion out of the card. */}
        <div className="min-w-0">
          <Heading className="font-display text-base font-medium text-ink">
            {title}
          </Heading>
          {subtitle != null && (
            <p className="mt-1 text-sm text-annotation">{subtitle}</p>
          )}
        </div>
      </div>
      {children != null && <div className="mt-4">{children}</div>}
    </PaperPanel>
  );
}
